import { DataServiceType, ServiceLayer, TileJson } from './types';
import {
	Info360TileMetadata,
	Info360TileMetadataV2,
	Layer,
	LayerV2,
} from './Info360TileTypes';
import { LngLatBounds, LngLatLike } from 'mapbox-gl';
import { convertCoordsToBounds, notUndefined } from '@Map/utils';

import AssetRegistry from './AssetRegistry';
import Info360TileService from './Info360TileService';
import MapboxDataSource from './MapboxDataSource';

export default class Info360TileServiceV2 extends Info360TileService {
	protected _bounds: LngLatBounds | undefined;
	// Store TileJson metadata for each system type to access in get layers()
	protected _tileJsonBySystemType: Map<string, TileJson> = new Map();

	constructor(dataService: DataServiceType<Info360TileMetadataV2>) {
		// hacky but no nice way of getting correct types
		super(dataService as DataServiceType<Info360TileMetadata>);
	}

	get bounds(): LngLatBounds | undefined {
		return this._bounds;
	}

	get dataEndpoint(): string | null {
		return `${this.endpoint}/v2/tileset`;
	}

	parseResponse(data: Info360TileMetadataV2): Info360TileMetadata | null {
		const layers = Object.values(data.systems).flat() as Layer[];
		return {
			...data,
			layers,
		};
	}

	createDataSources(data: Info360TileMetadata & Info360TileMetadataV2): void {
		if (!data.systems) return;

		const dataSources = Object.entries(data.systems).flatMap(
			([systemType, entry]) => {
				if (this.isTileJson(entry)) {
					// _mapTileJson now returns an array of sources (one per vector layer)
					return this._mapTileJson(entry as TileJson, systemType);
				} else {
					return this._mapTileMetaLayers(entry as LayerV2[]);
				}
			},
		);

		if (data?.bounds?.southWest && data?.bounds?.northEast) {
			this._bounds = new LngLatBounds(
				data.bounds.southWest,
				data.bounds.northEast,
			);
		} else {
			const coords = Object.values(data.systems)
				.flatMap(entry => {
					if (this.isTileJson(entry)) {
						const [swLng, swLat, neLng, neLat] = (entry as TileJson)
							.bounds;
						return [
							[swLng, swLat],
							[neLng, neLat],
						];
					}
				})
				.filter(notUndefined) as LngLatLike[];
			this._bounds = convertCoordsToBounds(coords);
		}

		this._dataSources = dataSources;
	}

	protected isTileJson(entry: TileJson | LayerV2[]) {
		return !Array.isArray(entry);
	}

	protected _mapTileJson(tileJson: TileJson, systemType: string) {
		// Store TileJson for later use in get layers()
		this._tileJsonBySystemType.set(systemType, tileJson);

		return new MapboxDataSource({
			type: `${AssetRegistry.getLayerType(systemType)}`,
			id: systemType,
			sourceLayer: systemType,
			// The next 3 fields are unused, in fact the return value is always undefined
			// since we are passing system type to a function that expects an asset type
			displayName: AssetRegistry.getDisplayName(systemType),
			icon: AssetRegistry.getIcon(systemType),
			color: AssetRegistry.getColor(systemType),
			minZoom: this._parseZoom(tileJson.minzoom),
			maxZoom: this._parseZoom(tileJson.maxzoom),
			tileUrl: tileJson.tiles,
		});
	}

	protected _mapTileMetaLayers(tileLayers: LayerV2[]) {
		return tileLayers.map(l => this.mapTileLayer(l));
	}

	protected getLayerId(layer: LayerV2): string {
		return `${layer.systemType}-${layer.type}`;
	}

	/**
	 * Override get layers() to properly expand vector_layers into separate ServiceLayers.
	 * Each vector_layer gets its own ServiceLayer with correct asset-type metadata.
	 */
	get layers(): ServiceLayer[] {
		const sources = this._dataSources as MapboxDataSource[];
		if (!sources || !sources.length) return [];

		return sources.flatMap(source => {
			const tileJson = this._tileJsonBySystemType.get(source.id);

			// If no TileJson metadata, fall back to parent behavior
			if (!tileJson?.vector_layers?.length) {
				console.warn(
					`[Info360TileServiceV2] TileJson not found or has no vector_layers for source: ${source.id}`,
				);
				return this._mapSourceToServiceLayer(source);
			}

			// Create one ServiceLayer per vector_layer
			return tileJson.vector_layers.map(vl => ({
				id: vl.id, // Asset type like "wwPipe"
				source: source.id, // System type like "SanitarySewer" (tile source)
				serviceId: this.id,
				displayName: AssetRegistry.getDisplayName(vl.id) ?? '',
				type: AssetRegistry.getLayerType(vl.id) ?? '',
				icon: AssetRegistry.getIcon(vl.id),
				color: AssetRegistry.getColor(vl.id),
				cluster: source.cluster,
				loaded: this.loaded,
				errored: this.errored,
				sourceLayer: vl.id,
			}));
		});
	}

	/**
	 * Fallback: Map a single MapboxDataSource to a ServiceLayer (for non-TileJson sources)
	 */
	protected _mapSourceToServiceLayer(source: MapboxDataSource) {
		return {
			id: source.id,
			source: source.id,
			serviceId: this.id,
			displayName: source.displayName ?? '',
			type: source.type ?? '',
			icon: source.icon,
			cluster: source.cluster,
			color: source.color,
			loaded: this.loaded,
			errored: this.errored,
			sourceLayer: source.sourceLayer,
		};
	}

	private _parseZoom(zoom: string | number | undefined) {
		if (!zoom) return;
		return +zoom;
	}
}
